import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { Avatar, ErrorBox, PriorityIcon, StatusBadge, TypeIcon, relTime } from '../components';
import { setLastList } from '../recent';
import type { IssueHeader } from '../types';
import { N_, t } from '../i18n';
import { GettingStarted } from './GettingStarted';

interface Box {
  total: number;
  issues: IssueHeader[];
  query: Record<string, string>;
}

// same boxes as Mantis' My View page
const BOXES: { key: string; title: string; accent: string }[] = [
  { key: 'assigned', title: N_('Assigned to Me (Unresolved)'), accent: '#0C66E4' },
  { key: 'unassigned', title: N_('Unassigned'), accent: '#E56910' },
  { key: 'reported', title: N_('Reported by Me'), accent: '#8F7EE7' },
  { key: 'resolved', title: N_('Resolved'), accent: '#1F845A' },
  { key: 'recent', title: N_('Recently Modified'), accent: '#626F86' },
  { key: 'monitored', title: N_('Monitored by Me'), accent: '#206A83' },
];

export function MyViewPage() {
  const { meta, projectId, project, statusColor } = useMeta();
  const colorRows = meta.prefs?.color_issue_rows !== false;
  const { data, error, isLoading } = useQuery({
    queryKey: ['myview', projectId],
    queryFn: () => api.get<Record<string, Box>>(`/api/myview${qs({ project: projectId || undefined })}`),
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t('My View')}</h1>
        <span className="subtle">{projectId ? project(projectId)?.name : t('All Projects')}</span>
      </div>
      <GettingStarted />
      <ErrorBox error={error} />
      {isLoading && <div className="subtle">{t('Loading…')}</div>}
      {data && (
        <div className="myview-grid">
          {BOXES.map((b) => {
            const box = data[b.key];
            return (
              <section key={b.key} className="card box" style={{ borderTopColor: b.accent }}>
                <div className="card-head">
                  <h2>
                    <Link to={'/issues' + qs({ ...box.query })}>{t(b.title)}</Link>
                  </h2>
                  <span className="count-pill">
                    {box.issues.length < box.total ? `${box.issues.length} / ${box.total}` : box.total}
                  </span>
                </div>
                <div className="box-list">
                  {box.issues.map((i) => (
                    <Link
                      key={i.id}
                      to={`/issues/${i.id}`}
                      className={`box-row${colorRows ? ' issue-status-row' : ''}`}
                      style={colorRows ? { '--issue-status-color': statusColor(i.status) } as CSSProperties : undefined}
                      onClick={() => setLastList(box.issues.map((x) => x.id))}
                    >
                      <span className="box-icons">
                        <TypeIcon severity={i.severity} />
                        <PriorityIcon priority={i.priority} />
                      </span>
                      <span className="box-main">
                        <span className="box-summary">
                          <span className="key">{String(i.id).padStart(7, '0')}</span> {i.summary}
                        </span>
                        <span className="subtle small">
                          {i.project_name}
                          {i.category_name ? ` · ${i.category_name}` : ''} · {relTime(i.updated_at)}
                        </span>
                      </span>
                      <span className="box-side">
                        <StatusBadge status={i.status} resolution={i.resolution} />
                        <Avatar name={i.handler_name} size={22} />
                      </span>
                    </Link>
                  ))}
                  {box.issues.length === 0 && <div className="box-empty">{t('Nothing here.')}</div>}
                </div>
                {box.total > box.issues.length && (
                  <Link className="box-more" to={'/issues' + qs({ ...box.query })}>
                    {t('View all {n} →', { n: box.total })}
                  </Link>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
